// src/components/shared/WarehouseSelector.tsx
// Component for selecting warehouse across the application

import React from 'react';
import { ChevronDown, Warehouse as WarehouseIcon, MapPin, Check, Globe } from 'lucide-react';
import { useWarehouse } from '../../contexts/WarehouseContext';
import { Warehouse } from '../../services/warehouseService';
import { useTranslation } from 'react-i18next';

interface WarehouseSelectorProps {
  className?: string;
  compact?: boolean;
  showIcon?: boolean;
  onWarehouseChange?: (warehouse: Warehouse | null) => void;
  allowSelectAll?: boolean;
}

export const WarehouseSelector: React.FC<WarehouseSelectorProps> = ({
  className = '',
  compact = false,
  showIcon = true,
  onWarehouseChange,
  allowSelectAll = false,
}) => {
  const { t } = useTranslation();
  const {
    selectedWarehouse,
    warehouses,
    loading,
    error,
    setSelectedWarehouse,
  } = useWarehouse();

  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleWarehouseSelect = (warehouse: Warehouse | null) => {
    setSelectedWarehouse(warehouse);
    setIsOpen(false);
    
    // Call external callback if provided
    if (onWarehouseChange) {
      onWarehouseChange(warehouse);
    }
  };

  const handleSelectAll = () => {
    handleWarehouseSelect(null); // null represents "all warehouses"
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {showIcon && <WarehouseIcon className="h-4 w-4 text-gray-400" />}
        <div className="animate-pulse bg-gray-200 h-6 w-32 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-2 text-red-600 ${className}`}>
        {showIcon && <WarehouseIcon className="h-4 w-4" />}
        <span className="text-sm">{t('warehouse.loadError')}</span>
      </div>
    );
  }

  if (warehouses.length === 0) {
    return (
      <div className={`flex items-center space-x-2 text-gray-500 ${className}`}>
        {showIcon && <WarehouseIcon className="h-4 w-4" />}
        <span className="text-sm">{t('warehouse.noWarehouses')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center space-x-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors
          ${compact ? 'px-2 py-1 text-sm' : 'px-3 py-2'}
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
        `}
      >
        {showIcon && (
          selectedWarehouse ? (
            <WarehouseIcon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500`} />
          ) : (
            <Globe className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-blue-500`} />
          )
        )}
        
        <div className="flex flex-col items-start min-w-0 flex-1">
          {selectedWarehouse ? (
            <>
              <span className={`font-medium text-gray-900 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                {selectedWarehouse.code}
              </span>
              {!compact && (
                <span className="text-xs text-gray-500 truncate">
                  {selectedWarehouse.name}
                </span>
              )}
            </>
          ) : (
            <span className={`text-blue-600 font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
              {t('warehouse.allWarehouses')}
            </span>
          )}
        </div>

        <ChevronDown 
          className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu - more compact when compact mode */}
      {isOpen && (
        <div
          className={`absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto right-0
            ${compact ? 'min-w-[180px] max-w-[220px] max-h-48 py-0.5' : 'w-full max-h-60 py-1'}`}
        >
          <div className={compact ? 'py-0.5' : 'py-1'}>
            {/* Option to select all warehouses */}
            {allowSelectAll && (
              <button
                onClick={handleSelectAll}
                className={`w-full text-left hover:bg-gray-50 flex items-center space-x-2 transition-colors border-b border-gray-100
                  ${compact ? 'px-2 py-1.5' : 'px-3 py-2'} ${compact ? 'space-x-2' : 'space-x-3'}`}
              >
                <div className="flex-shrink-0">
                  {!selectedWarehouse ? (
                    <Check className={compact ? 'h-3.5 w-3.5 text-blue-600' : 'h-4 w-4 text-blue-600'} />
                  ) : (
                    <div className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1.5">
                    <Globe className={compact ? 'h-3.5 w-3.5 text-blue-500' : 'h-4 w-4 text-blue-500'} />
                    <span className={`font-medium text-blue-600 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {t('warehouse.allWarehouses')}
                    </span>
                  </div>
                  {!compact && (
                    <div className="text-sm text-gray-500">
                      {t('warehouse.allWarehousesDescription')}
                    </div>
                  )}
                </div>
              </button>
            )}

            {/* Individual warehouse options */}
            {Array.isArray(warehouses) && warehouses.map((warehouse) => (
              <button
                key={warehouse.id}
                onClick={() => handleWarehouseSelect(warehouse)}
                className={`w-full text-left hover:bg-gray-50 flex items-center transition-colors ${compact ? 'px-2 py-1.5 space-x-2' : 'px-3 py-2 space-x-3'}`}
              >
                <div className="flex-shrink-0">
                  {warehouse.id === selectedWarehouse?.id ? (
                    <Check className={compact ? 'h-3.5 w-3.5 text-blue-600' : 'h-4 w-4 text-blue-600'} />
                  ) : (
                    <div className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1.5 flex-wrap">
                    <span className={`font-medium text-gray-900 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {warehouse.code}
                    </span>
                    {warehouse.is_default && (
                      <span className="px-1 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded">
                        {t('warehouse.default')}
                      </span>
                    )}
                  </div>
                  <div className={`text-gray-500 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                    {warehouse.name}
                  </div>
                  {!compact && warehouse.address && (
                    <div className="flex items-center space-x-1 text-xs text-gray-400 mt-0.5">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{warehouse.address}</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
            {(!Array.isArray(warehouses) || warehouses.length === 0) && (
              <div className={`text-gray-500 ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}>
                {t('warehouse.noWarehouses')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Compact version for headers/toolbars
export const CompactWarehouseSelector: React.FC<Omit<WarehouseSelectorProps, 'compact'>> = (props) => (
  <WarehouseSelector {...props} compact={true} allowSelectAll={false} />
);

// Simple warehouse indicator (read-only)
export const WarehouseIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { selectedWarehouse, loading } = useWarehouse();

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <WarehouseIcon className="h-4 w-4 text-gray-400" />
        <div className="animate-pulse bg-gray-200 h-4 w-20 rounded"></div>
      </div>
    );
  }

  if (!selectedWarehouse) {
    return (
      <div className={`flex items-center space-x-2 text-gray-500 ${className}`}>
        <WarehouseIcon className="h-4 w-4" />
        <span className="text-sm">{t('warehouse.noSelection')}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <WarehouseIcon className="h-4 w-4 text-blue-600" />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900">
          {selectedWarehouse.code}
        </span>
        <span className="text-xs text-gray-500">
          {selectedWarehouse.name}
        </span>
      </div>
    </div>
  );
};

/*
DOCUMENTACIÓN DE USO - FUNCIONALIDAD "TODOS LOS ALMACENES"

El componente WarehouseSelector ahora incluye la funcionalidad para seleccionar "todos los almacenes".
Esta funcionalidad permite ver datos combinados de todos los almacenes en lugar de un almacén específico.

CARACTERÍSTICAS:
- Opción "Todos los almacenes" aparece al principio del dropdown
- Cuando se selecciona "Todos los almacenes", selectedWarehouse es null
- El icono cambia a un globo (Globe) cuando se selecciona "Todos los almacenes"
- El texto se muestra en azul para indicar la selección global

PROPIEDADES:
- allowSelectAll: boolean (por defecto: true) - Controla si mostrar la opción "Todos los almacenes"
- onWarehouseChange: callback que recibe Warehouse | null - null representa "todos los almacenes"

EJEMPLO DE USO:
```tsx
<WarehouseSelector 
  allowSelectAll={true}
  onWarehouseChange={(warehouse) => {
    if (warehouse === null) {
      // Se seleccionó "Todos los almacenes"
      // Mostrando datos de todos los almacenes
    } else {
      // Se seleccionó un almacén específico
      // Mostrando datos del almacén específico
    }
  }}
/>
```

INTEGRACIÓN CON BACKEND:
- Cuando selectedWarehouse es null, el warehouse_id se envía como undefined al backend
- El backend debe manejar el caso cuando warehouse_id es undefined para devolver datos de todos los almacenes
- Los servicios ya están configurados para manejar este caso correctamente

TRADUCCIONES:
- Español: "Todos los almacenes" / "Ver datos de todos los almacenes combinados"
- Italiano: "Tutti i magazzini" / "Visualizza i dati di tutti i magazzini combinati"
*/
